import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { baseService } from './base.service'
import { CreateCommentDto, CreateMemeDto, UpdateMemeDto } from './dto/meme-dto.interface'
import { IBaseService } from './interfaces/base-service.interface'

import { QueryParams } from '@/types/base.type'
import { KeysetListResponse, ListResponse } from '@/types/list.type'
import { CommentMeme, EmojiData, HashtagMeme, MemePost, PinnedPost, UserEngagementStats } from '@/types/meme.interface'

function appendMemePostImageUrls(formData: FormData, imageUrls?: string[]): void {
  if (!imageUrls?.length) {
    return
  }

  if (imageUrls.length === 1) {
    formData.append('imageUrls', imageUrls[0])
    return
  }

  formData.append('imageUrls', JSON.stringify(imageUrls))
}

class MemeService {
  private static instance: MemeService
  private readonly api: AxiosInstance

  private constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  public static getInstance(baseService: IBaseService): MemeService {
    if (!MemeService.instance) {
      MemeService.instance = new MemeService(baseService)
    }
    return MemeService.instance
  }

  createMeme = async (memeData: CreateMemeDto, options?: AxiosRequestConfig) => {
    const formData = new FormData()
    formData.append('content', memeData.content)
    appendMemePostImageUrls(formData, memeData.imageUrls)
    memeData.imageFiles?.forEach(file => {
      formData.append('files', file)
    })
    if (memeData.hashtags) {
      memeData.hashtags.forEach(hashtag => {
        formData.append('hashtags', hashtag)
      })
    }
    if (memeData.isAnonymous) {
      formData.append('isAnonymous', memeData.isAnonymous.toString())
    }
    if (memeData.channelId) {
      formData.append('channelId', memeData.channelId)
    }
    memeData.mentionedUserIds?.forEach(userId => {
      formData.append('mentionedUserIds', String(userId))
    })

    const { data } = await this.api.post('/meme/posts', formData, {
      ...options,
      headers: {
        ...options?.headers,
        'Content-Type': 'multipart/form-data',
      },
    })
    return data
  }

  updateMeme = async (postId: string, memeData: UpdateMemeDto, options?: AxiosRequestConfig) => {
    const formData = new FormData()
    if (memeData.content) {
      formData.append('content', memeData.content)
    }
    if (memeData.hashtags) {
      memeData.hashtags.forEach(hashtag => {
        formData.append('hashtags', hashtag)
      })
    }
    if (memeData.imageUrls) {
      appendMemePostImageUrls(formData, memeData.imageUrls)
    }
    if (memeData.isAnonymous) {
      formData.append('isAnonymous', memeData.isAnonymous.toString())
    }
    memeData.mentionedUserIds?.forEach(userId => {
      formData.append('mentionedUserIds', String(userId))
    })

    const { data } = await this.api.patch(`/meme/posts/${postId}`, formData, options)
    return data
  }

  getMemes = async (query?: QueryParams, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<KeysetListResponse<MemePost>>('/meme/posts', {
      ...options,
      params: {
        ...query,
      },
    })
    return data
  }

  getChannelMemes = async (channelId: string, query?: QueryParams, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<KeysetListResponse<MemePost>>(`/meme/channels/${channelId}/posts`, {
      ...options,
      params: {
        ...query,
      },
    })
    return data
  }

  getMemeDetail = async (postId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<MemePost>(`/meme/posts/${postId}`, options)
    return data
  }

  getTrendingHashtags = async (limit?: number, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<Array<HashtagMeme>>(`/meme/trending?limit=${limit || 10}`, options)
    return data
  }

  getTrendingPosts = async (limit?: number, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<Array<MemePost>>(`/meme/trending/posts?limit=${limit || 10}`, options)
    return data
  }

  toggleReaction = async (
    { postId, reactionType = 'like', emojiData }: { postId: string; reactionType?: string; emojiData?: EmojiData },
    options?: AxiosRequestConfig
  ) => {
    const { data } = await this.api.post(`/meme/post-reaction/${postId}`, { reactionType, emojiData }, options)
    return data
  }

  removePostReaction = async (postId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.delete(`/meme/post-reaction/${postId}/remove`, options)
    return data
  }

  getVotedMemes = async (query?: QueryParams, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<ListResponse<MemePost>>('/meme/posts-my-reactions', {
      ...options,
      params: {
        ...query,
      },
    })
    return data
  }

  getTopUsersByEngagementForMemePost = async (limit?: number, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<Array<UserEngagementStats>>(
      `/meme/users-top-engagement?limit=${limit || 5}`,
      options
    )
    return data
  }

  getChannelUserStats = async (channelId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<Array<UserEngagementStats>>(`/meme/channels/${channelId}/user-stats`, options)
    return data
  }

  getMyMemeEngagementStats = async (options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<UserEngagementStats>('/meme/users/my-engagement-stats', options)
    return data
  }

  deleteMeme = async (postId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.delete(`/meme/posts/${postId}`, {
      ...options,
    })
    return data
  }

  getComments = async (postId: string, query?: QueryParams, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<ListResponse<CommentMeme>>(`/meme/post/${postId}/comments`, {
      ...options,
      params: {
        ...query,
      },
    })
    return data
  }

  createComment = async (
    { postId, content, parentId, isAnonymous, mentionedUserIds }: CreateCommentDto,
    options?: AxiosRequestConfig
  ) => {
    const { data } = await this.api.post(
      `/meme/comment`,
      { postId, content, parentId, isAnonymous, mentionedUserIds },
      options
    )
    return data
  }

  updateComment = async (commentId: string, content: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.patch(`/meme/comment/${commentId}`, { content }, options)
    return data
  }

  deleteComment = async (commentId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.delete(`/meme/comment/${commentId}`, {
      ...options,
    })
    return data
  }

  toggleCommentReaction = async (
    commentId: string,
    reactionType: string,
    emojiData?: EmojiData,
    options?: AxiosRequestConfig
  ) => {
    const { data } = await this.api.post(`/meme/comment/${commentId}/reaction`, { reactionType, emojiData }, options)
    return data
  }

  removeCommentReaction = async (commentId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.delete(`/meme/comment/${commentId}/remove-reaction`, options)
    return data
  }

  getCommentReactions = async (commentId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get(`/meme/comment/${commentId}/reactions`, options)
    return data
  }

  getCommentDetail = async (commentId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get(`/meme/comment/${commentId}/detail`, options)
    return data
  }

  // Pin post methods
  pinPost = async (channelId: string, postId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.post<PinnedPost>(`/meme/channels/${channelId}/pin/${postId}`, options)
    return data
  }

  getPinnedPosts = async (channelId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<PinnedPost[]>(`/meme/channels/${channelId}/pin`, options)
    return data
  }

  unpinPost = async (channelId: string, postId: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.delete<{ success: boolean }>(`/meme/channels/${channelId}/pin/${postId}`, options)
    return data
  }
}

const memeService = MemeService.getInstance(baseService)

export default memeService
